import classNames from './GenerateReport.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useEffect, useMemo, useState } from 'react';
import { StudentReportContent } from '@/school-management/types/response/studentDetails';
import Header from './Header/Header';
import TopCard from './TopCard/TopCard';
import Card from './Card/Card';
import BarGraph from './BarGraph/BarGraph';
import * as helper from './helper';
import { studentDetailsService } from '@/school-management/services/studentDetails';
import graphIcon from '@/assets/svg/graph.svg';
import learningIcon from '@/assets/svg/learning.svg';
import thumbRedIcon from '@/assets/svg/thumb-red.svg';
import engagementIcon from '@/assets/svg/engagement.svg';
import reportProgressIcon from '@/assets/svg/reportProgress.svg';
import streakLengthIcon from '@/assets/svg/streakLength.svg';
import attemptsIcon from '@/assets/svg/attempts.svg';
import skillScoresIcon from '@/assets/svg/score.svg';
interface StudentReportProps {
  schoolId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  generateReportPdf: (element: HTMLElement | null) => void;
  onLoadReportError: () => void;
}

const StudentReport = ({
  schoolId,
  studentId,
  firstName,
  lastName,
  generateReportPdf,
  onLoadReportError,
}: StudentReportProps) => {
  const [studentReport, setStudentReport] = useState<StudentReportContent | null>(null);
  const { t } = useAdminTranslation();

  useEffect(() => {
    let isActive = true;

    const loadReport = async () => {
      if (!schoolId || !studentId) {
        return;
      }

      try {
        const studentReport =
          (await studentDetailsService.getStudentReport(schoolId, studentId))?.data?.data || null;

        if (isActive) {
          setStudentReport(studentReport);
        }
      } catch (error) {
        if (isActive) {
          setStudentReport(null);
          onLoadReportError();
        }
      }
    };

    loadReport();

    return () => {
      isActive = false;
    };
  }, [schoolId, studentId, onLoadReportError]);

  const currentStudentData = useMemo(() => {
    const studentName = `${firstName} ${lastName}`;

    return {
      studentName,
    };
  }, [firstName, lastName]);

  const studentReportData = useMemo(() => {
    if (!studentReport) {
      return null;
    }

    const level = studentReport?.userLevel || 0;
    const lavelName = level ? t(`levelName_${level}`) : '';
    const overallProgressInLevel =
      helper.roundNumber(studentReport?.overallProgressInLevel || 0, 0).toString() + '%';
    const failure = helper.roundNumber(studentReport?.failureRate || 0, 0);
    const pass = 100 - failure;

    const failureRate = `${failure}%`;
    const passRate = `${pass}%`;

    const engagementItems: {
      label: string;
      value?: string;
    }[] = [
      {
        label: t('studentDataModal.report.total'),
        value: helper.timeString(studentReport?.engagement?.total || 0),
      },
      {
        label: t('studentDataModal.report.totalInPrev7Days'),
        value: helper.timeString(studentReport?.engagement?.totalInPrev7Days || 0),
      },
      {
        label: t('studentDataModal.report.avgPerWeek'),
        value: helper.timeString(studentReport?.engagement?.averagePerWeek || 0),
      },
    ];

    const progressItems: {
      label: string;
      value?: string;
    }[] = [
      {
        label: t('studentDataModal.report.total'),
        value:
          (studentReport?.hasSkippedActivities ? '* ' : '') +
          helper
            .roundNumber(studentReport?.progress?.totalActivitiesCompletedInLevel || 0, 2)
            .toString(),
      },
      {
        label: t('studentDataModal.report.previous7Days'),
        value: helper
          .roundNumber(studentReport?.progress?.totalActivitiesCompletedInTheLast7Days || 0, 2)
          .toString(),
      },
      {
        label: t('studentDataModal.report.avgPerWeek'),
        value: helper
          .roundNumber(studentReport?.progress?.averageActivitysPerWeek || 0, 2)
          .toString(),
      },
    ];

    const streakLengthItems: {
      label: string;
      value?: string;
    }[] = [
      {
        label: t('studentDataModal.report.longestStreak'),
        value: helper.roundNumber(studentReport?.streakLength?.longestStreak || 0, 2).toString(),
      },
      {
        label: t('studentDataModal.report.streakLast7Days'),
        value: helper.roundNumber(studentReport?.streakLength?.strakLast7Days || 0, 2).toString(),
      },
      {
        label: t('studentDataModal.report.avgStreakPerWeek'),
        value: helper
          .roundNumber(studentReport?.streakLength?.averageStreakPerWeek || 0, 2)
          .toString(),
      },
    ];

    const attemptsAndHintsItems: {
      label: string;
      value?: string;
    }[] = [
      {
        label: t('studentDataModal.report.avgAttemptsPerActivity'),
        value: helper
          .roundNumber(studentReport?.attemptsAndHints?.averageAttemptsPerActivity || 0, 2)
          .toString(),
      },
      {
        label: t('studentDataModal.report.totalHintsUsed'),
        value: helper
          .roundNumber(studentReport?.attemptsAndHints?.totalHintsUsed || 0, 2)
          .toString(),
      },
      {
        label: t('studentDataModal.report.avgHintsPerActivity'),
        value: helper
          .roundNumber(studentReport?.attemptsAndHints?.averageHintsPerActivity || 0, 2)
          .toString(),
      },
    ];

    const skippedComment = studentReport?.hasSkippedActivities
      ? t('studentDataModal.report.skippedActivitiesComment')
      : undefined;

    return {
      level,
      lavelName,
      overallProgressInLevel,
      passRate,
      failureRate,
      engagementItems,
      progressItems,
      streakLengthItems,
      attemptsAndHintsItems,
      skippedComment,
    };
  }, [studentReport, t]);

  const reportSkillsData = useMemo(() => {
    if (!studentReport) {
      return null;
    }

    const avgSkillScoresMap = (studentReport?.avgSkillsScore || []).reduce((map, item) => {
      map.set(item.skillId, item.skillScore);
      return map;
    }, new Map<number, number>());

    const avgSkillScoresItems: {
      label: string;
      value: number;
    }[] = [];

    helper.reportSkillsList.forEach(skill => {
      const label = t(`skills.${skill.name?.toLowerCase()}`);

      avgSkillScoresItems.push({
        label,
        value: helper.roundNumber(avgSkillScoresMap.get(skill.id || 0) || 0, 0),
      });
    });

    return {
      avgSkillScoresItems,
    };
  }, [studentReport, t]);

  return (
    studentReportData &&
    reportSkillsData && (
      <div ref={generateReportPdf} className={classNames.pdfContainer}>
        <Header
          items={[
            {
              label: t('studentDataModal.report.studentName'),
              value: currentStudentData.studentName,
            },
            {
              label: t('studentDataModal.report.level'),
              value: `${studentReportData.level} | ${studentReportData.lavelName}`,
            },
            {
              label: t('studentDataModal.report.date'),
              value: helper.dateString(new Date(), t('lang')),
            },
          ]}
        />
        <div className={classNames.contentContainer}>
          <TopCard
            type='student'
            items={[
              {
                icon: graphIcon,
                label:
                  (studentReport?.hasSkippedActivities ? '* ' : '') +
                  t('studentDataModal.report.overallProgressInLevel', {
                    level: studentReportData.level,
                  }),
                value: studentReportData.overallProgressInLevel,
              },
              {
                icon: learningIcon,
                label: t('studentDataModal.report.passRate'),
                value: studentReportData.passRate,
              },
              {
                icon: thumbRedIcon,
                label: t('studentDataModal.report.failureRate'),
                value: studentReportData.failureRate,
                color: '#FF0000',
              },
            ]}
          />
          <div className={classNames.lineContainer}>
            <Card
              width='266px'
              height='154px'
              titleIcon={engagementIcon}
              title={t('studentDataModal.report.engagement')}
              description={t('studentDataModal.report.timeSpentOnActivites')}
              items={studentReportData.engagementItems}
            />
            <Card
              width='266px'
              height='154px'
              titleIcon={reportProgressIcon}
              title={t('studentDataModal.report.progress')}
              description={t('studentDataModal.report.activitiesCompletedInLevel', {
                level: studentReportData.level,
              })}
              items={studentReportData.progressItems}
            />
          </div>
          <div className={classNames.lineContainer}>
            <Card
              width='266px'
              height='154px'
              titleIcon={streakLengthIcon}
              title={t('studentDataModal.report.streakLength')}
              description={t('studentDataModal.report.continuousEngagement')}
              items={studentReportData.streakLengthItems}
            />
            <Card
              width='266px'
              height='154px'
              titleIcon={attemptsIcon}
              title={t('studentDataModal.report.attemptsAndHints')}
              description={t('studentDataModal.report.inCurrentLevel')}
              items={studentReportData.attemptsAndHintsItems}
            />
          </div>
          <BarGraph
            width='552px'
            height='216px'
            titleIcon={skillScoresIcon}
            title={t('studentDataModal.report.avgSkillScores')}
            items={reportSkillsData.avgSkillScoresItems}
          />
          {studentReportData.skippedComment && (
            <div className={classNames.remark}>
              <span className={classNames.text}>{studentReportData.skippedComment}</span>
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default StudentReport;
